/**
 * 端末画面（MiraiOS）。
 *
 * コマンドは暗記不要。候補から選べるようにする。
 * 出力は必ず sim の計算結果を整形したもので、ハードコードはしない。
 */

import type { DeviceId, NetworkWorld } from '../types/network';
import {
  formatIpconfig,
  formatNslookup,
  formatPing,
  formatTraceroute,
  ipconfig,
  nslookup,
  ping,
  traceroute,
  type TerminalLine,
} from '../sim';
import { button, clear, el } from './dom';
import { usesTouchControls } from './touchControls';

export interface TerminalCommandSpec {
  name: string;
  usage: string;
  description: string;
  /** 引数を取るか */
  takesTarget: boolean;
}

export const COMMANDS: TerminalCommandSpec[] = [
  {
    name: 'ping',
    usage: 'ping <宛先>',
    description: '相手に「聞こえますか」と信号を送り、返事が返るか確かめる',
    takesTarget: true,
  },
  {
    name: 'ipconfig',
    usage: 'ipconfig',
    description: '自分の IP アドレス設定を表示する',
    takesTarget: false,
  },
  {
    name: 'nslookup',
    usage: 'nslookup <名前>',
    description: '名前から IP アドレスを引く（DNS への問い合わせ）',
    takesTarget: true,
  },
  {
    name: 'traceroute',
    usage: 'traceroute <宛先>',
    description: '相手までの経路を 1 ホップずつ表示する',
    takesTarget: true,
  },
  { name: 'help', usage: 'help', description: '使えるコマンドの一覧を表示する', takesTarget: false },
  { name: 'clear', usage: 'clear', description: '画面を消す', takesTarget: false },
];

export interface TerminalHooks {
  onCommandExecuted: (command: string, argument: string, success: boolean) => void;
  onType: () => void;
  onClose: () => void;
}

export class TerminalScreen {
  readonly root: HTMLElement;
  private output: HTMLElement;
  private input: HTMLInputElement;
  private suggestionBox: HTMLElement;
  private commandBar: HTMLElement;
  private context: HTMLElement;
  private history: string[] = [];
  private historyIndex = -1;
  private suggestions: string[] = [];
  private selectedSuggestion = 0;

  private world: NetworkWorld | null = null;
  private deviceId: DeviceId = '';

  constructor(private readonly hooks: TerminalHooks) {
    this.output = el('div', { class: 'term-output' });
    this.input = el('input', {
      class: 'term-input',
      type: 'text',
      spellcheck: 'false',
      autocomplete: 'off',
      autocapitalize: 'off',
      autocorrect: 'off',
      enterkeyhint: 'send',
      placeholder: 'コマンドを選ぶか、入力してください（Tab で補完）',
    });
    this.suggestionBox = el('div', { class: 'term-suggestions', hidden: true });
    this.commandBar = el('div', { class: 'term-commandbar' });
    this.context = el('div', { class: 'term-context' });

    const head = el('div', { class: 'term-head' }, [
      el('span', { class: 'term-title', text: 'MiraiOS ターミナル' }),
      button('閉じる (T / Esc)', () => this.hooks.onClose(), 'btn btn-ghost btn-small'),
    ]);

    const inputRow = el('div', { class: 'term-inputrow' }, [
      el('span', { class: 'term-prompt', text: '>' }),
      this.input,
      button('実行', () => { this.execute(this.input.value); this.input.blur(); }, 'btn btn-small'),
    ]);

    this.root = el('div', { class: 'term-layer', hidden: true }, [
      el('div', { class: 'term-window' }, [
        head,
        this.context,
        this.output,
        this.suggestionBox,
        inputRow,
        this.commandBar,
      ]),
    ]);

    this.input.addEventListener('input', () => {
      this.hooks.onType();
      this.updateSuggestions();
    });
    this.input.addEventListener('keydown', (event) => this.handleKeyDown(event));
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  attachWorld(world: NetworkWorld, deviceId: DeviceId): void {
    if (this.world !== world || this.deviceId !== deviceId) {
      clear(this.output);
      this.input.value = '';
      this.history = [];
      this.historyIndex = -1;
      this.suggestions = [];
      this.selectedSuggestion = 0;
      clear(this.suggestionBox);
      this.suggestionBox.hidden = true;
    }
    this.world = world;
    this.deviceId = deviceId;
    this.context.textContent = `実行元PC：${world.devices[deviceId]?.label ?? deviceId}`;
  }

  open(): void {
    this.root.hidden = false;
    if (this.output.childElementCount === 0) this.printWelcome();
    this.renderCommandBar();
    this.updateSuggestions();
    if (!usesTouchControls()) window.setTimeout(() => { if (this.isOpen) this.input.focus(); }, 30);
  }

  close(): void {
    this.input.blur();
    this.root.hidden = true;
    this.suggestionBox.hidden = true;
  }

  // ------------------------------------------------------------
  // 出力
  // ------------------------------------------------------------

  private printWelcome(): void {
    this.printLines([
      { text: 'MiraiOS 端末 v1.0  — 株式会社ミライ商事', tone: 'ok' },
      { text: '', tone: 'normal' },
      { text: 'コマンドは覚えなくて構いません。下のボタンから選べます。', tone: 'hint' },
      { text: '"help" と入力すると、使えるコマンドの一覧が出ます。', tone: 'hint' },
      { text: '', tone: 'normal' },
    ]);
  }

  private printLines(lines: TerminalLine[]): void {
    for (const line of lines) {
      this.output.append(
        el('div', { class: `term-line term-${line.tone}`, text: line.text || ' ' }),
      );
    }
    this.output.scrollTop = this.output.scrollHeight;
  }

  private printEcho(command: string): void {
    this.output.append(
      el('div', { class: 'term-line term-echo' }, [
        el('span', { class: 'term-prompt', text: '>' }),
        document.createTextNode(' ' + command),
      ]),
    );
  }

  // ------------------------------------------------------------
  // 補完
  // ------------------------------------------------------------

  /** ping / nslookup などの宛先候補。プレイヤーが暗記しなくて済むようにする */
  private targetCandidates(): string[] {
    const world = this.world;
    if (!world) return [];
    const set = new Set<string>();

    for (const id of Object.keys(world.devices)) {
      const device = world.devices[id];
      for (const [hostname, address] of Object.entries(device.dnsRecords ?? {})) {
        set.add(hostname);
        set.add(address);
      }
    }
    const me = world.devices[this.deviceId];
    if (me) {
      if (me.ip.gateway) set.add(me.ip.gateway);
      for (const dns of me.ip.dnsServers) set.add(dns);
    }
    // 社内機器は「見えている」ものとして候補に出す（学習の障害を減らすため）
    for (const id of Object.keys(world.devices)) {
      const device = world.devices[id];
      if (device.kind === 'internet') continue;
      if (device.ip.address) set.add(device.ip.address);
    }
    return [...set].sort();
  }

  private updateSuggestions(): void {
    const value = this.input.value;
    const parts = value.split(/\s+/);
    const isFirstToken = parts.length <= 1;
    const current = (parts[parts.length - 1] ?? '').toLowerCase();

    let pool: string[];
    if (isFirstToken) {
      pool = COMMANDS.map((c) => c.name);
    } else {
      const command = COMMANDS.find((c) => c.name === parts[0].toLowerCase());
      pool = command?.takesTarget ? this.targetCandidates() : [];
    }

    this.suggestions = pool.filter((item) => item.toLowerCase().startsWith(current)).slice(0, 12);
    this.selectedSuggestion = 0;
    this.renderSuggestions();
  }

  private renderSuggestions(): void {
    clear(this.suggestionBox);
    if (this.suggestions.length === 0 || this.input.value.trim().length === 0) {
      this.suggestionBox.hidden = true;
      return;
    }
    this.suggestionBox.hidden = false;
    this.suggestions.forEach((item, index) => {
      const node = el('button', {
        class: index === this.selectedSuggestion ? 'term-suggestion selected' : 'term-suggestion',
        type: 'button',
        text: item,
      });
      node.addEventListener('click', (event) => {
        event.preventDefault();
        this.applySuggestion(item);
      });
      this.suggestionBox.append(node);
    });
  }

  private applySuggestion(item: string): void {
    const parts = this.input.value.split(/\s+/);
    parts[parts.length - 1] = item;
    const command = COMMANDS.find((c) => c.name === parts[0].toLowerCase());
    this.input.value = parts.join(' ') + (command?.takesTarget && parts.length === 1 ? ' ' : '');
    this.input.focus();
    this.updateSuggestions();
  }

  private renderCommandBar(): void {
    clear(this.commandBar);
    this.commandBar.append(el('span', { class: 'term-commandbar-label', text: 'コマンド:' }));
    for (const command of COMMANDS) {
      const node = button(
        command.name,
        () => {
          this.input.value = command.takesTarget ? `${command.name} ` : command.name;
          this.input.focus();
          this.updateSuggestions();
          if (!command.takesTarget) this.execute(this.input.value);
        },
        'btn btn-small btn-term',
      );
      node.title = `${command.usage} — ${command.description}`;
      this.commandBar.append(node);
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // 端末に入力中は、ゲーム側のショートカットへ流さない
    event.stopPropagation();

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hooks.onClose();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const pick = this.suggestions[this.selectedSuggestion];
      if (pick) this.applySuggestion(pick);
      return;
    }
    if (event.key === 'ArrowDown' && !this.suggestionBox.hidden) {
      event.preventDefault();
      this.selectedSuggestion = (this.selectedSuggestion + 1) % this.suggestions.length;
      this.renderSuggestions();
      return;
    }
    if (event.key === 'ArrowUp' && !this.suggestionBox.hidden) {
      event.preventDefault();
      this.selectedSuggestion =
        (this.selectedSuggestion - 1 + this.suggestions.length) % this.suggestions.length;
      this.renderSuggestions();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.recallHistory(-1);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.recallHistory(1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.execute(this.input.value);
    }
  }

  private recallHistory(direction: number): void {
    if (this.history.length === 0) return;
    this.historyIndex = Math.min(
      this.history.length - 1,
      Math.max(0, this.historyIndex + direction),
    );
    this.input.value = this.history[this.history.length - 1 - this.historyIndex] ?? '';
    this.updateSuggestions();
  }

  // ------------------------------------------------------------
  // 実行
  // ------------------------------------------------------------

  execute(raw: string): void {
    const line = raw.trim();
    this.input.value = '';
    this.suggestionBox.hidden = true;
    if (line.length === 0) return;

    this.history.push(line);
    this.historyIndex = -1;
    this.printEcho(line);

    const [name, ...rest] = line.split(/\s+/);
    const command = name.toLowerCase();
    const argument = rest.join(' ');
    const world = this.world;

    if (command === 'clear') {
      clear(this.output);
      this.hooks.onCommandExecuted(command, argument, true);
      return;
    }
    if (command === 'help') {
      this.printLines([
        { text: '使えるコマンド:', tone: 'normal' },
        ...COMMANDS.map<TerminalLine>((c) => ({
          text: `  ${c.usage.padEnd(22, ' ')} ${c.description}`,
          tone: 'normal',
        })),
        { text: '', tone: 'normal' },
        { text: '宛先は Tab キーか下の候補から選べます。', tone: 'hint' },
        { text: '', tone: 'normal' },
      ]);
      this.hooks.onCommandExecuted(command, argument, true);
      return;
    }

    if (!world) {
      this.printLines([{ text: 'ネットワークに接続されていません。', tone: 'error' }]);
      return;
    }

    const spec = COMMANDS.find((c) => c.name === command);
    if (!spec) {
      this.printLines([
        { text: `'${name}' は認識されていないコマンドです。`, tone: 'error' },
        { text: 'help と入力すると一覧が出ます。', tone: 'hint' },
        { text: '', tone: 'normal' },
      ]);
      this.hooks.onCommandExecuted(command, argument, false);
      return;
    }
    if (spec.takesTarget && argument.length === 0) {
      this.printLines([
        { text: `使い方: ${spec.usage}`, tone: 'warn' },
        { text: '宛先を指定してください（下の候補から選べます）。', tone: 'hint' },
        { text: '', tone: 'normal' },
      ]);
      return;
    }

    let success = true;
    switch (command) {
      case 'ping': {
        const result = ping(world, this.deviceId, argument);
        success = result.failureReason === null;
        this.printLines(formatPing(world, result));
        break;
      }
      case 'ipconfig':
        this.printLines(formatIpconfig(ipconfig(world, this.deviceId)));
        break;
      case 'nslookup': {
        const result = nslookup(world, this.deviceId, argument);
        success = result.failureReason === null;
        this.printLines(formatNslookup(result));
        break;
      }
      case 'traceroute': {
        const hops = traceroute(world, this.deviceId, argument);
        success = hops.every((hop) => hop.rttMs !== null);
        this.printLines(formatTraceroute(argument, hops));
        break;
      }
    }
    this.printLines([{ text: '', tone: 'normal' }]);
    this.hooks.onCommandExecuted(command, argument, success);
  }
}
